import type { TFunction } from 'i18next';

import { isDeletedColumnKey } from './excelDeletedColumn';
import { isIdColumnKey, pickRowId, readExcelFile, splitImportRowsById } from './tableExcelExport';

export type ParsedImportBatch<TCreate, TUpdatePayload> = {
  creates: TCreate[];
  updates: Array<{ id: string; payload: TUpdatePayload }>;
  errors: string[];
};

export async function parseExcelImportBatch<TCreate, TUpdatePayload>(options: {
  file: File;
  existingIds: Set<string>;
  t: TFunction;
  mapCreateRow: (row: Record<string, unknown>, line: number) => { item: TCreate | null; errors: string[] };
  mapUpdateRow: (
    row: Record<string, unknown>,
    line: number,
    id: string,
  ) => { payload: TUpdatePayload | null; errors: string[] };
}): Promise<ParsedImportBatch<TCreate, TUpdatePayload>> {
  const rawData = await readExcelFile(options.file);
  const split = splitImportRowsById(rawData, options.existingIds);
  const errors: string[] = [];

  for (let index = 0; index < rawData.length; index += 1) {
    const row = rawData[index];
    const id = split.updates.find((entry) => entry.row === row)?.id
      ?? (() => {
        const rowId = pickRowId(row);
        return rowId && split.missingIds.includes(rowId) ? rowId : null;
      })();
    if (id && !options.existingIds.has(id)) {
      errors.push(
        options.t('common.import.idNotFound', { line: index + 2, id }),
      );
    }
  }

  const creates: TCreate[] = [];
  for (let index = 0; index < split.creates.length; index += 1) {
    const row = split.creates[index];
    const line = rawData.indexOf(row) + 2;
    const mapped = options.mapCreateRow(row, line);
    if (mapped.errors.length > 0) {
      errors.push(...mapped.errors);
    } else if (mapped.item) {
      creates.push(mapped.item);
    }
  }

  const updates: Array<{ id: string; payload: TUpdatePayload }> = [];
  for (const entry of split.updates) {
    const line = rawData.indexOf(entry.row) + 2;
    const mapped = options.mapUpdateRow(entry.row, line, entry.id);
    if (mapped.errors.length > 0) {
      errors.push(...mapped.errors);
    } else if (mapped.payload) {
      updates.push({ id: entry.id, payload: mapped.payload });
    }
  }

  return { creates, updates, errors };
}

export function mergeDefinedFields<T extends Record<string, unknown>>(
  base: T,
  patch: Partial<T>,
  presentKeys: string[],
): T {
  const merged = { ...base };
  for (const key of presentKeys) {
    if (key in patch && patch[key as keyof T] !== undefined) {
      merged[key as keyof T] = patch[key as keyof T] as T[keyof T];
    }
  }
  return merged;
}

export function presentRowKeys(row: Record<string, unknown>): string[] {
  return Object.keys(row).filter(
    (key) =>
      !isIdColumnKey(key)
      && !isDeletedColumnKey(key)
      && row[key] !== undefined
      && row[key] !== '',
  );
}

export function readRowCell(row: Record<string, unknown>, ...headers: string[]): unknown {
  for (const header of headers) {
    const value = row[header];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }
  return undefined;
}

export function formKeysPresentInRow<T extends Record<string, unknown>>(
  row: Record<string, unknown>,
  headerToFormKey: Record<string, keyof T & string>,
): (keyof T & string)[] {
  return Object.entries(headerToFormKey)
    .filter(([header]) => readRowCell(row, header) !== undefined)
    .map(([, formKey]) => formKey);
}
